import { useState, useEffect, useRef } from 'react';
import { useLocalParticipant, useParticipants, useRoomContext } from '@livekit/components-react';
import { Track, RoomEvent, ParticipantEvent } from 'livekit-client';

/**
 * Temporary debug panel for diagnosing audio issues.
 * Shows mic state, audio levels, track info, and room audio status.
 */
export function AudioDebugPanel() {
  const { localParticipant } = useLocalParticipant();
  const participants = useParticipants();
  const room = useRoomContext();

  const [micLevel, setMicLevel] = useState(0);
  const [canPlayAudio, setCanPlayAudio] = useState(true);
  const [logs, setLogs] = useState<string[]>([]);
  const [expanded, setExpanded] = useState(true);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const addLog = (msg: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [`[${timestamp}] ${msg}`, ...prev].slice(0, 50));
    console.log(`[AudioDebug] ${msg}`);
  };

  // Get local audio track info
  const audioTrack = localParticipant
    ?.getTrackPublications()
    .find((pub) => pub.kind === Track.Kind.Audio);

  const isMicEnabled = audioTrack?.track && !audioTrack.isMuted;
  const trackSid = audioTrack?.trackSid || 'none';
  const trackSource = audioTrack?.source || 'none';

  // Monitor room audio playback status
  useEffect(() => {
    if (!room) return;

    const checkAudio = () => {
      const canPlay = room.canPlaybackAudio;
      setCanPlayAudio(canPlay);
      addLog(`Audio playback allowed: ${canPlay}`);
    };

    checkAudio();
    room.on(RoomEvent.AudioPlaybackStatusChanged, checkAudio);

    return () => {
      room.off(RoomEvent.AudioPlaybackStatusChanged, checkAudio);
    };
  }, [room]);

  // Monitor local participant audio events
  useEffect(() => {
    if (!localParticipant) return;

    const onTrackPublished = (pub: any) => {
      if (pub.kind === Track.Kind.Audio) {
        addLog(`Local audio track published: ${pub.trackSid}`);
      }
    };

    const onTrackUnpublished = (pub: any) => {
      if (pub.kind === Track.Kind.Audio) {
        addLog(`Local audio track unpublished: ${pub.trackSid}`);
      }
    };

    const onTrackMuted = (pub: any) => {
      if (pub.kind === Track.Kind.Audio) {
        addLog(`Local audio track muted`);
      }
    };

    const onTrackUnmuted = (pub: any) => {
      if (pub.kind === Track.Kind.Audio) {
        addLog(`Local audio track unmuted`);
      }
    };

    localParticipant.on(ParticipantEvent.TrackPublished, onTrackPublished);
    localParticipant.on(ParticipantEvent.TrackUnpublished, onTrackUnpublished);
    localParticipant.on(ParticipantEvent.TrackMuted, onTrackMuted);
    localParticipant.on(ParticipantEvent.TrackUnmuted, onTrackUnmuted);

    addLog(`Local participant: ${localParticipant.identity}`);
    addLog(`Mic enabled: ${localParticipant.isMicrophoneEnabled}`);
    addLog(`Camera enabled: ${localParticipant.isCameraEnabled}`);

    return () => {
      localParticipant.off(ParticipantEvent.TrackPublished, onTrackPublished);
      localParticipant.off(ParticipantEvent.TrackUnpublished, onTrackUnpublished);
      localParticipant.off(ParticipantEvent.TrackMuted, onTrackMuted);
      localParticipant.off(ParticipantEvent.TrackUnmuted, onTrackUnmuted);
    };
  }, [localParticipant]);

  // Mic level meter using Web Audio API
  useEffect(() => {
    if (!audioTrack?.track || audioTrack.isMuted) {
      setMicLevel(0);
      return;
    }

    const track = audioTrack.track;
    const mediaStream = new MediaStream([track.mediaStreamTrack]);

    try {
      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;

      const analyser = audioContext.createAnalyser();
      analyserRef.current = analyser;
      analyser.fftSize = 256;

      const source = audioContext.createMediaStreamSource(mediaStream);
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateLevel = () => {
        if (!analyserRef.current) return;
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
        setMicLevel(Math.min(100, average * 2));
        requestAnimationFrame(updateLevel);
      };

      updateLevel();
      addLog('Mic level meter started');
    } catch (err) {
      addLog(`Mic level meter error: ${err}`);
    }

    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
        analyserRef.current = null;
      }
    };
  }, [audioTrack?.track, audioTrack?.isMuted]);

  // Get remote participants with audio
  const remoteWithAudio = participants
    .filter(p => !p.isLocal)
    .map(p => {
      const audioTracks = p.getTrackPublications().filter(t => t.kind === Track.Kind.Audio);
      return {
        identity: p.identity,
        name: p.name,
        isSpeaking: p.isSpeaking,
        audioLevel: p.audioLevel,
        audioTracks: audioTracks.map(t => ({
          sid: t.trackSid,
          muted: t.isMuted,
          subscribed: t.isSubscribed,
        })),
      };
    });

  const testMic = async () => {
    addLog('Testing mic enable...');
    try {
      await localParticipant?.setMicrophoneEnabled(true);
      addLog('Mic enable request sent');
    } catch (err) {
      addLog(`Mic enable error: ${err}`);
    }
  };

  const testAudioPlayback = async () => {
    addLog('Testing audio playback...');
    try {
      await room?.startAudio();
      addLog('startAudio() called');
    } catch (err) {
      addLog(`startAudio error: ${err}`);
    }
  };

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="fixed bottom-4 left-4 z-50 px-3 py-2 bg-black/80 text-white text-xs rounded-lg border border-white/20"
      >
        Show Audio Debug
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 left-4 z-50 w-80 max-h-[70vh] overflow-hidden bg-black/90 text-white text-xs rounded-lg border border-white/20 shadow-xl flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10">
        <span className="font-bold">Audio Debug Panel</span>
        <button onClick={() => setExpanded(false)} className="text-white/60 hover:text-white">
          ✕
        </button>
      </div>

      <div className="overflow-y-auto p-3 space-y-3">
        {/* Room Status */}
        <div className="space-y-1">
          <div className="font-semibold text-white/70">Room Status</div>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${canPlayAudio ? 'bg-green-500' : 'bg-red-500'}`} />
            <span>Audio Playback: {canPlayAudio ? 'Allowed' : 'Blocked'}</span>
          </div>
          {!canPlayAudio && (
            <button
              onClick={testAudioPlayback}
              className="px-2 py-1 bg-blue-600 rounded text-white"
            >
              Enable Audio Playback
            </button>
          )}
        </div>

        {/* Local Mic Status */}
        <div className="space-y-1">
          <div className="font-semibold text-white/70">Local Microphone</div>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${isMicEnabled ? 'bg-green-500' : 'bg-red-500'}`} />
            <span>Status: {isMicEnabled ? 'Enabled' : 'Disabled/Muted'}</span>
          </div>
          <div className="text-white/50">Track SID: {trackSid}</div>
          <div className="text-white/50">Source: {trackSource}</div>

          {/* Mic Level */}
          <div className="flex items-center gap-2">
            <span>Level:</span>
            <div className="flex-1 h-2 bg-white/20 rounded overflow-hidden">
              <div
                className="h-full bg-green-500 transition-all duration-75"
                style={{ width: `${micLevel}%` }}
              />
            </div>
            <span className="w-8 text-right">{Math.round(micLevel)}%</span>
          </div>

          <button
            onClick={testMic}
            className="px-2 py-1 bg-blue-600 rounded text-white"
          >
            Test Enable Mic
          </button>
        </div>

        {/* Remote Participants */}
        <div className="space-y-1">
          <div className="font-semibold text-white/70">Remote Participants ({remoteWithAudio.length})</div>
          {remoteWithAudio.length === 0 ? (
            <div className="text-white/50">No remote participants</div>
          ) : (
            remoteWithAudio.map(p => (
              <div key={p.identity} className="p-2 bg-white/5 rounded">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${p.isSpeaking ? 'bg-green-500 animate-pulse' : 'bg-white/30'}`} />
                  <span>{p.name || p.identity}</span>
                </div>
                <div className="text-white/50 ml-4">
                  Audio level: {(p.audioLevel * 100).toFixed(0)}%
                </div>
                {p.audioTracks.map(t => (
                  <div key={t.sid} className="text-white/50 ml-4">
                    Track: {t.sid?.slice(-6) || 'none'} |
                    Muted: {t.muted ? 'Y' : 'N'} |
                    Subscribed: {t.subscribed ? 'Y' : 'N'}
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        {/* Event Log */}
        <div className="space-y-1">
          <div className="font-semibold text-white/70">Event Log</div>
          <div className="max-h-32 overflow-y-auto bg-white/5 rounded p-2 font-mono text-[10px]">
            {logs.length === 0 ? (
              <div className="text-white/50">No events yet</div>
            ) : (
              logs.map((log, i) => (
                <div key={i} className="text-white/70">{log}</div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
